import React from 'react';
import { Home, Users, HeartHandshake } from 'lucide-react';

const ServiceCard = ({ icon: Icon, title, description }) => (
    <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-[#008080] hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center group h-full">
        <div className="w-20 h-20 bg-[#008080] rounded-full flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform duration-300">
            <Icon size={40} strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-bold text-[#008080] mb-4 font-serif tracking-wide">{title}</h3>
        <p className="text-gray-500 leading-relaxed text-sm">
            {description}
        </p>
    </div>
);

const Services = () => {
    const services = [
        {
            icon: Home,
            title: "Supported Independent Living (SIL)",
            description: "Personalized in-home support to help you live as independently as possible while receiving the care you need 24/7."
        },
        {
            icon: Users,
            title: "Community Participation",
            description: "Engage with your community through social events, skill-building workshops, and group activities tailored to your interests."
        },
        {
            icon: HeartHandshake,
            title: "Daily Personal Activities",
            description: "Assistance with daily living tasks such as personal hygiene, meal preparation, and medication management to enhance your wellbeing."
        }
    ];

    return (
        <React.Fragment>
            {/* What is NDIS Section - Part of Home Flow in Design */}
            <section className="py-20 bg-white" id="ndis-info">
                <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-12">
                    <div className="w-full md:w-1/2">
                        <img
                            src="/assets/young-adult-helping-disabled-friend.jpg"
                            alt="What is NDIS"
                            className="rounded-3xl shadow-2xl w-full object-cover h-[400px]"
                        />
                    </div>
                    <div className="w-full md:w-1/2 bg-gray-50 p-10 rounded-3xl shadow-sm border border-gray-100">
                        <h2 className="text-[#D99C3B] font-serif text-4xl font-bold mb-6">What is NDIS?</h2>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            The National Disability Insurance Scheme (NDIS) provides support to eligible people with intellectual, physical, sensory, cognitive and psychosocial disability. Early intervention supports can also be provided for eligible people with disability or children with developmental delay.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            NDIS gives you more choice and control over how, when and where your supports are provided, and gives you certainty you will get the support you need.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-gray-50 scroll-mt-32" id="services">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-[#D99C3B] font-serif text-4xl md:text-5xl font-bold mb-4">Our Services</h2>
                        <div className="w-24 h-1 bg-[#008080] mx-auto rounded-full opacity-50"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
                        {services.map((service, index) => (
                            <ServiceCard key={index} {...service} />
                        ))}
                    </div>

                    {/* NDIS Support Categories Section */}
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-[#D99C3B] font-serif text-4xl md:text-5xl font-bold mb-4">NDIS Support Categories</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { code: "0102", title: "Assistance to Access and Maintain Employment or Higher Education" },
                                { code: "0106", title: "Assistance in Coordinating or Managing Life Stages, Transitions and Supports" },
                                { code: "0107", title: "Daily Personal Activities" },
                                { code: "0108", title: "Assistance with Travel/Transport Arrangements" },
                                { code: "0115", title: "Assistance with Daily Life Tasks in a Group or Shared Living Arrangement" },
                                { code: "0116", title: "Innovative Community Participation" },
                                { code: "0117", title: "Development of Daily Living and Life Skills" },
                                { code: "0120", title: "Household Tasks" },
                                { code: "0125", title: "Participation in Community, Social & Civic Activities" },
                                { code: "0136", title: "Group and Centre Based Activities" }
                            ].map((item, index) => (
                                <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 flex flex-col justify-center">
                                    <span className="text-2xl font-light text-[#008080] mb-2 block">{item.code}</span>
                                    <h3 className="text-gray-700 font-medium text-lg leading-snug">{item.title}</h3>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    );
};

export default Services;
